import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { getBotMonitor, setBotMonitorMail } from '../../../api/botMonitorApi';
const names = {
  mix: 'MIX',
  transports: 'Transporty MIX',
  full_pallet: 'Pełne palety',
  production_handover: 'Przekazania produkcji',
};
const states = {
  OK: ['Działa prawidłowo', 'success'],
  WARNING: ['Wymaga uwagi', 'warning'],
  OFFLINE: ['Brak kontaktu', 'error'],
  NO_DATA: ['Brak danych', 'default'],
  RUNNING: ['W trakcie', 'info'],
  COMPLETED: ['Poprawnie', 'success'],
  FAILED: ['Błąd', 'error'],
  INTERRUPTED: ['Przerwane', 'error'],
};
const stages = {
  'process_mix_deliveries.py': 'Analiza MIX',
  'process_mix_transports.py': 'Import transportów',
  'process_production_plan.py': 'Import planu produkcji',
  'process_transport_stock.py': 'Import zapasu',
  'process_full_pallet_simulation.py': 'Symulacja pełnych palet',
  'run_production_handover_bot.py': 'Przekazania produkcji',
};
const time = (value) =>
  value
    ? new Date(value).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })
    : '—';
const pct = (value) =>
  value == null
    ? 'Brak danych'
    : `${value.toLocaleString('pl-PL', { maximumFractionDigits: 2 })}%`;
function Status({ value }) {
  const [label, color] = states[value] || ['Oczekuje', 'default'];
  return <Chip size="small" label={label} color={color} />;
}
function Availability({ title, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 220 }}>
      <Typography>{title}</Typography>
      <Typography variant="h5">{pct(value?.percent)}</Typography>
      <Typography variant="body2">
        Brak kontaktu: {value?.offlineMinutes ?? 0} min · Brak danych:{' '}
        {value?.unknownMinutes ?? 0} min
      </Typography>
      {value && (
        <Typography variant="caption">Pomiar od {time(value.from)}</Typography>
      )}
    </Paper>
  );
}
export default function BotMonitorPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true,
      timer;
    const load = async () => {
      try {
        const next = await getBotMonitor(page + 1);
        if (active) {
          setData(next);
          setError('');
        }
      } catch {
        if (active)
          setError(
            'Nie udało się odświeżyć monitoringu. Widoczne dane mogą być nieaktualne.',
          );
      } finally {
        if (active) timer = setTimeout(load, 15000);
      }
    };
    load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [page]);
  const changeMail = async (_, checked) => {
    setSaving(true);
    try {
      await setBotMonitorMail(checked);
      setData((old) => ({ ...old, notificationsEnabled: checked }));
      setError('');
    } catch {
      setError('Nie udało się zmienić ustawienia powiadomień.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Status botów
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!data ? (
        <Typography>Wczytywanie…</Typography>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Status value={data.status} />
            <Typography>
              Magazyn {data.warehouse} · Ostatni kontakt:{' '}
              {time(data.lastHeartbeatAt)}
            </Typography>
          </Stack>
          {data.status === 'NO_DATA' && (
            <Alert severity="info">
              Monitoring oczekuje na pierwszy sygnał z AMBF BOT. Uruchom
              integrację na komputerze z harmonogramem.
            </Alert>
          )}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ my: 2 }}
          >
            <Availability title="Dostępność dzisiaj" value={data.daily} />
            <Availability
              title="Dostępność w tym miesiącu"
              value={data.monthly}
            />
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography>Skuteczność zadań w miesiącu</Typography>
              <Typography variant="h5">{pct(data.effectiveness)}</Typography>
              <Typography variant="body2">
                Zakończone poprawnie / wszystkie zakończone
              </Typography>
            </Paper>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Dostępność dotyczy kontaktu z BOT-em. Okresy bez pomiaru AMBF nie
            obniżają procentu. Czas: Europe/Warsaw. Odświeżanie co 15 sekund.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={data.notificationsEnabled}
                onChange={changeMail}
                disabled={saving}
              />
            }
            label="Powiadomienia mailowe o awariach i przywróceniu działania"
          />
          {!data.notificationsEnabled && (
            <Alert severity="info">
              Maile są wyłączone. Monitoring i historia nadal działają.
            </Alert>
          )}
          {data.notificationsEnabled && !data.mailGroupConfigured && (
            <Alert severity="warning">
              Administrator musi utworzyć aktywną grupę „AMBF BOT — awarie i
              przywrócenie działania” dla tego magazynu i dodać odbiorców.
            </Alert>
          )}
          <Typography variant="h6" sx={{ mt: 3 }}>
            Zadania
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'Proces',
                    'Stan / etap',
                    'Ostatni sukces',
                    'Następny termin',
                  ].map((x) => (
                    <TableCell key={x}>{x}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{names[job.id] || job.id}</TableCell>
                    <TableCell>
                      {!job.enabled ? (
                        'Wyłączone w harmonogramie'
                      ) : (
                        <>
                          <Status
                            value={
                              job.problem
                                ? 'WARNING'
                                : job.waiting
                                ? 'WAITING'
                                : job.run?.status
                            }
                          />
                          <Typography variant="body2">
                            {job.problem ||
                              (job.run?.status === 'RUNNING'
                                ? stages[job.run.stage] || job.run.stage
                                : '')}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>{time(job.lastSuccess?.finishedAt)}</TableCell>
                    <TableCell>
                      {job.enabled ? time(job.nextAt) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Aktualność danych w AMBF
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Dane</TableCell>
                  <TableCell>Ostatnia poprawna aktualizacja</TableCell>
                  <TableCell>Stan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.freshness.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{time(row.at)}</TableCell>
                    <TableCell>
                      {row.stale
                        ? 'Sprawdź aktualność po wykonaniu zadania'
                        : row.at
                        ? 'Zapis potwierdzony w AMBF'
                        : 'Brak importu'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Problemy i powiadomienia
          </Typography>
          {!data.incidents.length && (
            <Typography>Brak zarejestrowanych problemów.</Typography>
          )}
          {data.incidents.map((incident) => (
            <Alert
              key={incident.id}
              severity={incident.closedAt ? 'success' : 'warning'}
              sx={{ mb: 1 }}
            >
              {incident.message}
              <br />
              {time(incident.openedAt)} ·{' '}
              {incident.closedAt
                ? `Zakończono: ${time(incident.closedAt)}`
                : 'Problem trwa'}
              {incident.mailError && (
                <Typography variant="body2" color="error">
                  {incident.mailError}
                </Typography>
              )}
              {(incident.alertStatus === 'SENDING' ||
                incident.recoveryStatus === 'SENDING') && (
                <Typography variant="body2">
                  Wysyłka w toku lub niepotwierdzona — nie jest automatycznie
                  ponawiana.
                </Typography>
              )}
            </Alert>
          ))}
          <Typography variant="h6" sx={{ mt: 3 }}>
            Historia wykonań
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'Proces / termin',
                    'Wynik',
                    'Rozpoczęcie',
                    'Czas trwania',
                    'Etap / informacja',
                  ].map((x) => (
                    <TableCell key={x}>{x}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.runs.map((run) => (
                  <TableRow key={run.runId}>
                    <TableCell>
                      {names[run.jobId] || run.jobId}
                      <br />
                      {time(run.slot)}
                    </TableCell>
                    <TableCell>
                      <Status value={run.status} />
                    </TableCell>
                    <TableCell>{time(run.startedAt)}</TableCell>
                    <TableCell>
                      {run.finishedAt && run.startedAt
                        ? `${Math.max(
                            0,
                            Math.round(
                              (new Date(run.finishedAt) -
                                new Date(run.startedAt)) /
                                1000,
                            ),
                          )} s`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {stages[run.stage] || run.stage}
                      <Typography variant="body2">{run.message}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} z ${count}`
            }
          />
        </>
      )}
    </Box>
  );
}
